/**
 * §A.8 · Hoy: la rutina de cada mañana, ya hecha · 13-10-2026
 *
 * El manual del back office (§2) pedía cinco minutos al día abriendo cinco
 * pestañas: Estado, Procesos, A quién llamar, Comercial y Fichas reclamadas, y cada
 * semana dos más. Esta pantalla las abre por nosotros y enseña solo lo que tiene
 * algo, con el botón para ir a resolverlo. Es a donde lleva la puerta.
 *
 * Arriba a la derecha, el estado del servicio: es el paso uno de la rutina, y se
 * comparte la consulta con *Operación → Estado* (misma clave), así que abrir las dos
 * no pregunta dos veces.
 */
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { BASE } from '@/shared/api/client';
import { aProblema } from '@/shared/api/errores';
import { useYo } from '@/shared/auth/sesionContexto';
import { BotonDelManual } from '@/shared/ui/BotonDelManual';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { Icono } from '@/shared/ui/Icono';
import { Acceso, Saludo } from '@/shared/ui/PiezasDeInicio';
import { ListaDePendientes } from '@/shared/ui/pendientes/ListaDePendientes';
import {
  aPendientesDeKiap,
  usePendientesDeKiap,
} from '@/backoffice/pendientes';

/** El estado del servicio, en una pastilla que lleva a su pantalla. */
function EstadoDelServicioEnBreve({ alPulsar }: { alPulsar: () => void }) {
  const estado = useQuery({
    queryKey: ['admin', 'estado-del-servicio'],
    refetchInterval: 60_000,
    retry: false,
    queryFn: async () => {
      // `503` con el cuerpo puesto es una respuesta: ver `Estado.tsx`.
      const r = await fetch(new URL('/v1/health/deep', BASE));
      return (await r.json()) as { status: 'ok' | 'degraded' };
    },
  });

  const bien = estado.data?.status === 'ok';
  const texto = estado.isPending
    ? 'Comprobando…'
    : estado.isError
      ? 'El API no contesta'
      : bien
        ? 'Todo en pie'
        : 'Algo está caído';

  return (
    <button
      type="button"
      className={`estado-breve ${estado.isPending ? '' : bien ? 'estado-breve-bien' : 'estado-breve-mal'}`}
      onClick={alPulsar}
    >
      <Icono nombre="pulso" tamano={16} />
      {texto}
    </button>
  );
}

export function Hoy() {
  const yo = useYo();
  const pendientes = usePendientesDeKiap();
  const navegar = useNavigate();
  const ir = (destino: string) => void navegar({ to: destino });
  const lista = pendientes.data
    ? aPendientesDeKiap(pendientes.data.pending)
    : [];

  return (
    <section className="inicio">
      <Saludo nombre={yo.user.first_name}>
        <EstadoDelServicioEnBreve
          alPulsar={() => ir('/admin/operacion/estado')}
        />
      </Saludo>

      <h2 className="seccion">
        Pendiente
        {lista.length > 0 && (
          <span className="seccion-numero">{lista.length}</span>
        )}
      </h2>
      {pendientes.isPending ? (
        <Cargando filas={3} etiqueta="Mirando lo pendiente" />
      ) : pendientes.isError ? (
        <PanelError
          problema={aProblema(pendientes.error)}
          reintentar={() => void pendientes.refetch()}
        />
      ) : (
        <ListaDePendientes pendientes={lista} alIr={ir} />
      )}

      <h2 className="seccion">Accesos rápidos</h2>
      <div className="accesos-rapidos">
        <Acceso
          icono="mas-uno"
          texto="Anotar un lead"
          alPulsar={() => ir('/admin/comercial')}
        />
        <Acceso
          icono="cuenta"
          texto="Buscar a una persona"
          alPulsar={() => ir('/admin/soporte/personas')}
        />
        <Acceso
          icono="clubes"
          texto="Ver los clubes"
          alPulsar={() => ir('/admin/clubes')}
        />
        <Acceso
          icono="informes"
          texto="Ver las métricas"
          alPulsar={() => ir('/admin/dinero/metricas')}
        />
      </div>

      <p className="inicio-pie">
        Lo de cada semana y cada mes está en el manual, §2.{' '}
        <BotonDelManual de="backoffice" />
      </p>
    </section>
  );
}
